import { useEffect, useState } from 'react';
import type { NotificationRecipient, NotificationRecipientsUpsertWarning } from '@kawai/shared';
import { Button } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';
import { useNotificationRecipients } from '../lib/hooks/useNotificationRecipients.js';
import { useAllUsers } from '../lib/hooks/useAllUsers.js';
import {
  callNotificationRecipientsUpsert,
  notificationRecipientsUpsertErrorMessage,
} from '../lib/api/notificationRecipientsUpsert.js';
import { RecipientsTable } from '../components/recipients/RecipientsTable.js';
import { RecipientForm } from '../components/recipients/RecipientForm.js';
import { RecipientsPreview } from '../components/recipients/RecipientsPreview.js';

/**
 * N-15b: tenant-portal「通知先登録」。管理者向け通知（予約 / 決済 / HOT / 運用）の受信者を
 * 1 名簿で編集する。編集は tenant-admin / 兼任 saas-admin のみ（tenant-staff と閲覧専用
 * impersonation は閲覧のみ）。保存は Callable の全量置換（`notificationRecipientsUpsert`）。
 *
 * このフェーズでは既存の通知はまだ名簿を見ない（切替は N-15c）。運用者は先に名簿を作っておける。
 * S-136: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・表は ZebraTable・注意は warning の面）。
 */

const CARD = 'mt-6 rounded-xl bg-white px-[30px] py-[26px]';
const CARD_TITLE = 'text-h-card font-semibold text-ink';
const WARN = 'rounded-lg bg-warning-tint px-[18px] py-3 text-label text-warning-ink';
export function NotificationRecipientsPage() {
  const { role, tenantId, readOnly } = useAuth();
  const remote = useNotificationRecipients(tenantId);
  // 名簿の読込に失敗した状態で編集を許すと、保存（全量置換）が既存の名簿を空で上書きしうる。
  // 読込が終わる（doc 無しも含む）まで編集不可にする。
  const canManage =
    !!tenantId &&
    !readOnly &&
    remote.status === 'ready' &&
    (role === 'tenant-admin' || role === 'saas-admin');
  const [draft, setDraft] = useState<NotificationRecipient[]>([]);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState<{ open: boolean; target?: NotificationRecipient }>({
    open: false,
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<NotificationRecipientsUpsertWarning[]>([]);

  // 読み込み完了時に draft を初期化（編集中の draft は上書きしない）。
  useEffect(() => {
    if (remote.status === 'ready' && !dirty) {
      setDraft(remote.doc?.recipients ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote.status, remote.doc]);

  // users は編集モーダルを初めて開くまで読まない（閲覧だけなら顧客一覧に触れない）。
  const [usersWanted, setUsersWanted] = useState(false);
  const users = useAllUsers(tenantId, usersWanted);

  const openNew = () => {
    setUsersWanted(true);
    setEditing({ open: true });
  };
  const openEdit = (r: NotificationRecipient) => {
    setUsersWanted(true);
    setEditing({ open: true, target: r });
  };

  const upsertDraft = (r: NotificationRecipient) => {
    setDraft((prev) => {
      const i = prev.findIndex((x) => x.id === r.id);
      if (i < 0) return [...prev, r];
      return prev.map((x) => (x.id === r.id ? r : x));
    });
    setDirty(true);
    setEditing({ open: false });
  };
  const removeDraft = (r: NotificationRecipient) => {
    if (!window.confirm(`「${r.name}」を通知先から削除しますか？（保存するまで確定しません）`)) {
      return;
    }
    setDraft((prev) => prev.filter((x) => x.id !== r.id));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await callNotificationRecipientsUpsert({ recipients: draft });
      setDraft(res.recipients);
      setWarnings(res.warnings);
      setSavedAt(res.updatedAt);
      setDirty(false);
      remote.setDoc({
        tenantId: tenantId ?? '',
        recipients: res.recipients,
        createdAt: remote.doc?.createdAt ?? res.updatedAt,
        updatedAt: res.updatedAt,
        updatedBy: remote.doc?.updatedBy ?? '',
      });
    } catch (err) {
      setSaveError(notificationRecipientsUpsertErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // warnings は「最後に保存した名簿」に対するものなので、破棄（= 保存済みへ戻す）でも残す。
  const discard = () => {
    setDraft(remote.doc?.recipients ?? []);
    setDirty(false);
    setSaveError(null);
  };

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-h-page font-semibold text-ink">通知先登録</h1>
      <p className="mt-1 text-body text-ink-2">
        予約・決済・HOT 案件・運用アラートの管理者向け通知を、誰にメール / LINE
        で届けるかをここで決めます。
      </p>

      {!tenantId ? (
        <p className={`mt-6 ${WARN}`}>テナント管理者アカウントでログインしてください。</p>
      ) : (
        <>
          <section className={CARD}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className={CARD_TITLE}>名簿</h2>
              {canManage && (
                <Button type="button" onClick={openNew} disabled={saving}>
                  通知先を追加
                </Button>
              )}
            </div>
            {!canManage && remote.status !== 'loading' && remote.status !== 'error' && (
              <p className={`mt-3 ${WARN}`}>
                通知先の編集はテナント管理者のみ可能です（閲覧のみ）。
              </p>
            )}
            <div className="mt-4">
              {remote.status === 'loading' ? (
                <p className="text-body text-ink-3">読み込み中…</p>
              ) : remote.status === 'error' ? (
                <p role="alert" className="text-body text-danger-ink">
                  名簿を読み込めませんでした。再読み込みしてください（読み込めるまで編集はできません）。
                </p>
              ) : (
                <RecipientsTable
                  recipients={draft}
                  warnings={warnings}
                  canEdit={canManage && !saving}
                  onEdit={openEdit}
                  onRemove={removeDraft}
                />
              )}
            </div>
            {warnings.length > 0 && (
              <ul className="mt-4 list-disc rounded-lg bg-warning-tint py-3 pr-[18px] pl-9 text-label text-warning-ink" data-testid="save-warnings">
                {warnings.map((w) => (
                  <li key={`${w.recipientId}:${w.code}`}>{w.message}</li>
                ))}
              </ul>
            )}
            {canManage && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button type="button" onClick={() => void save()} disabled={!dirty || saving}>
                  {saving ? '保存中…' : '保存'}
                </Button>
                <Button type="button" variant="secondary" onClick={discard} disabled={!dirty || saving}>
                  変更を破棄
                </Button>
                {dirty && (
                  <span className="text-label text-warning-ink">未保存の変更があります</span>
                )}
                {!dirty && savedAt && (
                  <span className="text-label text-success-ink">保存しました</span>
                )}
              </div>
            )}
            {saveError && (
              <p role="alert" className="mt-3 text-body text-danger-ink">
                {saveError}
              </p>
            )}
          </section>

          <section className={CARD}>
            <h2 className={CARD_TITLE}>この設定で届く人</h2>
            <p className="mt-1 text-label text-ink-3">
              無効の人・チェックの無い種類は除いた結果です。{dirty && '（未保存の内容を含みます）'}
            </p>
            <div className="mt-4">
              <RecipientsPreview recipients={draft} />
            </div>
          </section>

          {editing.open && (
            <RecipientForm
              key={editing.target?.id ?? 'new'}
              open
              initial={editing.target}
              users={users.users}
              usersStatus={users.status}
              onCancel={() => setEditing({ open: false })}
              onSubmit={upsertDraft}
            />
          )}
        </>
      )}
    </div>
  );
}
